import { FieldConfig, useField, useFormikContext } from 'formik';
import { DateUtils } from '@jaam/commons';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import MuiDatePicker from './MuiDatePicker';

interface IProps extends FieldConfig {
  label: string;
  minDate?: Date;
  maxDate?: Date;
  placeholder?: string | null;
  stepName?: string;
  isError?: boolean;
  required?: boolean;
  disabled?: boolean;
  shouldAutoUpdate?: boolean;
  updateCallback?: (...args: any) => Promise<any> | any;
  refreshCallback?: (...args: any) => Promise<any> | any;
  onCalenderCallback?: (...args: any) => Promise<any> | any;
  children?: any;
}

export function FormikDateField<T>({
  label,
  minDate,
  maxDate,
  placeholder,
  stepName,
  isError,
  required,
  disabled,
  shouldAutoUpdate,
  updateCallback,
  refreshCallback,
  onCalenderCallback,
  ...props
}: IProps): any {
  const [field, meta, helpers] = useField(props);
  const [colorDateInputBlueState, setColorBlueState] = useState({});
  const [colorDateInputGreenState, setColorGreenState] = useState({});
  const [colorDateInputRedState, setColorRedState] = useState({});
  const { handleBlur, validateField, setFieldTouched, isSubmitting, errors } =
    useFormikContext<T>();
  const { setValue, setError } = helpers;
  const { initialValue, value, error, touched } = meta;
  const { name } = field;
  const inputReference = useRef(name);
  const [currentErrors, setCurrentErrors] = useState<any>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const moment1 = DateUtils(initialValue);
    const moment2 = DateUtils(value);
    if (
      !moment1.isSame(moment2, 'day') &&
      !(value === null || initialValue === null)
    ) {
      setColorBlueState({ [name]: name });
    } else {
      setColorBlueState({});
    }

    return () => {
      setColorGreenState({});
    };
  }, [value, initialValue, name]);

  useEffect(() => {
    if (currentErrors) {
      const namesError = Object.keys(currentErrors);
      const nameError = namesError[0];
      if (meta.touched && meta.error && nameError === name && currentErrors) {
        (inputReference.current as unknown as HTMLInputElement).focus();
        setCurrentErrors(null);
      }
    }
  }, [currentErrors, meta.touched, meta.error, name]);

  useEffect(() => {
    if (isSubmitting) setCurrentErrors(errors);
  }, [isSubmitting, meta.touched, meta.error, errors]);

  const processOnBlur = async (newValue: any) => {
    if (!error) {
      let newVal: string | null = newValue;

      const moment1 = DateUtils(initialValue);
      const moment2 = DateUtils(value);

      if (
        !moment1.isSame(moment2, 'day') &&
        !(value === null && initialValue === null) &&
        shouldAutoUpdate
      ) {
        if (newVal === '') {
          newVal = null;
        }
        const updateObject = { [name]: newVal };
        await updateProcess(updateObject);
      }
    }
  };

  const onChange = (newValue: any) => {
    if (newValue) {
      setFieldTouched(name);
      setValue(newValue);
    } else {
      setValue(null);
    }
  };

  const onDaySelected = async (newValue: any) => {
    setValue(newValue);

    if (!error) {
      setColorRedState({});
      const moment1 = DateUtils(initialValue);
      const moment2 = DateUtils(newValue);
      if (!moment1.isSame(moment2, 'day') && shouldAutoUpdate) {
        const updateObject = { [name]: newValue };
        await updateProcess(updateObject);
      }
    }
  };

  const updateProcess = async (data: any) => {
    try {
      if (updateCallback) {
        setIsUpdating(true);
        await updateCallback(data);
        setIsUpdating(false);
        setColorGreenState({ [name]: true });
      }

      setColorBlueState({});
      setTimeout(() => {
        setColorGreenState({});
        setColorRedState({});
      }, 2000);
    } catch (err: any) {
      setIsUpdating(false);
      setColorRedState({ [name]: true });
      setError(err.data);
      toast.error('An error occured', err);
    }
  };

  return (
    <div className=" mb-4">
      <MuiDatePicker
        minDate={minDate}
        maxDate={maxDate}
        innerRef={inputReference}
        required={required}
        disabled={disabled || !navigator.onLine}
        onChange={onChange}
        onBlur={(v) => {
          processOnBlur(v.target.value);
        }}
        onDateSelected={onDaySelected}
        isUpdating={isUpdating}
        color={
          (name in colorDateInputBlueState && 'primary') ||
          (name in colorDateInputGreenState && 'success') ||
          (name in colorDateInputRedState && 'error') ||
          undefined
        }
        name={name}
        error={(!!error || isError) && touched}
        helperText={touched ? error : ''}
        placeHolder={placeholder || ''}
        label={placeholder || ''}
        value={value}
        onKeyDown={() => setFieldTouched(name)}
      />
    </div>
  );
}

FormikDateField.defaultProps = {
  stepName: '',
};
FormikDateField.defaultProps = {
  updateCallback: () => ({}),
  refreshCallback: () => ({}),
  stepName: '',
};

export default FormikDateField;
